import React from 'react';
import { Box, Checkbox, Divider, FormControlLabel, FormLabel, ToggleButton, ToggleButtonGroup } from '@mui/material';

import { ChaseSettings, ChaseType, ComponentSide } from '@paragon/contract-library';
import { DistanceField } from '@paragon/material';
import { makeCache } from '@paragon/utilities';

import { LabeledBox } from './LabeledBox';

interface Props {
  readonly disabled: boolean;

  readonly value: ChaseSettings;
  readonly onChange: (value: ChaseSettings) => void;
}

export class ChaseSettingsPanel extends React.Component<Props> {
  widthHandleChange = makeCache((chaseType: ChaseType) => (event: React.ChangeEvent<{}>, checked: boolean) => {
    if (!checked) {
      return;
    }
    if (chaseType === 'FixedWidth') {
      this.updateSettings({ chaseMinWidth: this.props.value.chaseMaxWidth, chaseType });
    } else {
      this.updateSettings({ chaseType });
    }
  });

  onChange(key: keyof ChaseSettings) {
    return (value: number) => {
      this.updateSettings({ [key]: value });
    };
  }

  handleLocationChange = (event: React.ChangeEvent<{}>, value: string | null) => {
    if (value === null) return;
    this.updateSettings({ chaseLocationType: value as ChaseSettings['chaseLocationType'] });
  };

  sideHandleChange = (event: React.ChangeEvent<{}>, value: string | null) => {
    if (value === null) return;
    this.updateSettings({ chaseBaseSide: value as ComponentSide });
  };

  onChangeWidth = (value: number) => {
    this.updateSettings({ chaseMaxWidth: value, chaseMinWidth: value });
  };

  checkedChange = (name: keyof ChaseSettings) => (event: React.ChangeEvent<HTMLInputElement>) => {
    this.updateSettings({ [name]: event.target.checked });
  };

  updateSettings(updates: Partial<ChaseSettings>) {
    this.props.onChange({
      ...this.props.value,
      ...updates,
    });
  }

  render() {
    const { value, disabled } = this.props;

    const variableWidthChase = this.props.value.chaseType === 'VariableWidth';
    const fixedWidthChase = this.props.value.chaseType === 'FixedWidth';

    return (
      <LabeledBox title="Chase Settings" contentSx={{ display: 'flex', flexDirection: 'column', gap: 1, padding: 1 }}>
        <FormControlLabel
          control={<Checkbox checked={value.noChase} onChange={this.checkedChange('noChase')} disabled={disabled} />}
          label="No Chase"
        />

        {value.noChase === false && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <FormLabel>Location</FormLabel>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={value.chaseLocationType}
                onChange={this.handleLocationChange}
              >
                <ToggleButton sx={{ width: 75 }} value="Floating" disabled={disabled}>
                  Floating
                </ToggleButton>
                <ToggleButton sx={{ width: 75 }} value="Centered" disabled={disabled}>
                  Centered
                </ToggleButton>
                <ToggleButton sx={{ width: 75 }} value="Fixed" disabled={disabled}>
                  Fixed
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <FormLabel>Width</FormLabel>
              <ToggleButtonGroup exclusive size="small" value={this.props.value.chaseType}>
                <ToggleButton
                  onClick={this.widthHandleChange('VariableWidth')}
                  sx={{ width: 125 }}
                  value="VariableWidth"
                  disabled={disabled}
                >
                  Variable Width
                </ToggleButton>

                <ToggleButton
                  onClick={this.widthHandleChange('FixedWidth')}
                  sx={{ width: 125 }}
                  value="FixedWidth"
                  disabled={disabled}
                >
                  Fixed Width
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Divider />

            {value.chaseLocationType === 'Fixed' && (
              <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                <DistanceField
                  label="Fixed Location"
                  value={value.chaseLocation}
                  onSubmit={this.onChange('chaseLocation')}
                  disabled={value.chaseLocationType !== 'Fixed' || disabled}
                />

                <Box sx={{ display: 'flex', flexDirection: 'column', marginBottom: 1 }}>
                  <FormLabel>From Side</FormLabel>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={value.chaseBaseSide}
                    onChange={this.sideHandleChange}
                  >
                    <ToggleButton
                      sx={{ width: 75 }}
                      value="Left"
                      disabled={value.chaseLocationType !== 'Fixed' || disabled}
                    >
                      Left
                    </ToggleButton>

                    <ToggleButton
                      sx={{ width: 75 }}
                      value="Right"
                      disabled={value.chaseLocationType !== 'Fixed' || disabled}
                    >
                      Right
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Box>
            )}

            {this.props.value.chaseType === 'VariableWidth' ? (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <DistanceField
                  label="Min Width"
                  value={value.chaseMinWidth}
                  onSubmit={this.onChange('chaseMinWidth')}
                  disabled={!variableWidthChase || disabled}
                />
                <DistanceField
                  label="Max Width"
                  value={value.chaseMaxWidth}
                  onSubmit={this.onChange('chaseMaxWidth')}
                  disabled={!variableWidthChase || disabled}
                />
              </Box>
            ) : this.props.value.chaseType === 'FixedWidth' ? (
              <DistanceField
                label="Width"
                sx={{ marginTop: 1 }}
                value={value.chaseMaxWidth}
                onSubmit={this.onChangeWidth}
                disabled={!fixedWidthChase || disabled}
              />
            ) : null}
          </>
        )}
      </LabeledBox>
    );
  }
}
